/**
 * WitnessReplaySystem — play a level's archived solver witness in the LIVE game.
 *
 * Why
 * ---
 * `gen.proof` claims a level is winnable and `witnesses/{uuid}-{seed}-{rev}.json.gz`
 * is the receipt, but every existing check replays that receipt through an
 * *offline* engine — `AtomicSimulator` (the solver's own model) or
 * `BotStrategy.simulateLap` (see `WitnessReplay.ts`). Neither one is the code
 * the player actually runs: the real game has a physical conveyor, per-frame
 * shooting, and lap timing the offline engines collapse into one atomic step.
 *
 * This module closes that last gap. It turns a witness into the same inputs a
 * player makes — tap a queue lane, tap a stack slot — and feeds them to
 * `GameScene`'s own dispatch paths. A WIN is then end-to-end offline/online
 * parity; anything else is a precisely-located divergence in the real engine.
 *
 * Witnesses are event traces (ordered flow)
 * -----------------------------------------
 * Under `docs/ordered-flow-semantics.md` a trace is a sequence of two events:
 * `dispatch` (tap a lane head or a stack slot) and `settle` (the oldest rider's
 * lap completes and it arrives on the stack). The driver realizes a trace by
 * PACING: it emits a tap for each dispatch and, for a settle, simply waits until
 * the rider in question has left the in-flight ring. Riders may overlap freely
 * in between — the ring *is* the concurrency.
 *
 * Three witness vintages, told apart by their contents alone — see
 * {@link classifyWitness}, which is what decides the driver's mode so no caller
 * has to pass a flag:
 *
 *   Native — carries explicit `settle` actions; produced by the solver once it
 *       adopted ordered flow. Replayed verbatim, implied settles OFF. A
 *       burst-era native witness may have relied on dispatching into a full
 *       belt; live, the driver simply waits for belt room (the arrivals the
 *       burst forced happen at real lap-ends first).
 *   Strict-model — dispatch-only. Proved against the forced-advance rule where a
 *       survivor lands on the stack in the same step as its lap. That is a
 *       *conservative* ordered-flow trace: it never bursts and it arrives every
 *       survivor at the earliest moment. It maps in by inserting an implied
 *       `settle` after any dispatch that produced a survivor, which is what
 *       {@link WitnessDriverOptions.impliedSettle} does (on by default).
 *   Legacy — carries `{ type: 'advance' }`, the retired free conveyor→stack move
 *       off a parking pen the game never had. The driver still *reads* those so
 *       it can say so, but a caller should refuse to replay one.
 *       {@link witnessHasAdvanceActions} is that check.
 *
 * A witness cannot be both native and legacy: `advance` was deleted before
 * `settle` was added, so no solver revision ever emitted both. If one somehow
 * contains both, {@link classifyWitness} calls it legacy — reject wins.
 *
 * Readiness, not timers
 * ---------------------
 * Every dispatch waits for the game to actually be ready for it: the target
 * attacker sits at a dispatchable lane head or in a stack slot, the belt has
 * room for its slotCount (the classic rule — a tap into a full belt is
 * REFUSED, so the driver waits for the next lap-end to free the room; the
 * burst/eviction mechanic is retired), no dispatch animation is mid-flight,
 * and any implied settle has landed.
 *
 * Everything here is Phaser-free and side-effect-free: the driver reads a
 * {@link ReplayView} snapshot and returns a {@link ReplayCommand}. `GameScene`
 * owns the thin glue that builds the snapshot and performs the command.
 */

import type { LegacySolverAction, SolverAction, SolverState } from './PerfectSolver/SolverState';
import { OrderedFlowEngine } from './OrderedFlowEngine';

/** Console prefix for every line this replay emits. */
export const REPLAY_LOG_PREFIX = '[witness-replay]';

/**
 * Mirrors `PerfectSolver/witnessArchive.ts`'s `WITNESS_FORMAT_VERSION`. Copied
 * rather than imported because that module pulls in `node:fs`/`node:zlib` and
 * can't be bundled for the browser; `WitnessReplaySystem.test.ts` pins the two
 * together so they can't drift.
 */
export const WITNESS_FORMAT_VERSION = 1;

/**
 * One archived proof transcript, as stored (gzipped) under `witnesses/`.
 *
 * Typed with {@link LegacySolverAction} so a pre-forced-advance archive still
 * parses — the point is to recognise it and say so, not to choke on it. New
 * proofs only ever contain {@link SolverAction}s.
 */
export interface WitnessRecord {
  v: number;
  uuid: string;
  seed: number;
  solverRev: string;
  witness: LegacySolverAction[];
}

/**
 * Does this witness use the retired `advance` action? True means it was proved
 * against the pre-2026-09-01 model, where a lap survivor parked on the conveyor
 * until the player scheduled a free conveyor→stack move. Replaying such a line
 * against the live game proves nothing about either engine — the level needs a
 * fresh proof at the current `solverRev`.
 */
export function witnessHasAdvanceActions(actions: readonly LegacySolverAction[]): boolean {
  return actions.some(a => a.type === 'advance');
}

/**
 * Narrowing check: is this a current-model witness (no retired `advance`)?
 *
 * The inverse of {@link witnessHasAdvanceActions}, as a type predicate, so a
 * caller that has established the format can hand the actions to code typed on
 * {@link SolverAction}.
 */
export function isCurrentModelWitness(
  actions: readonly LegacySolverAction[],
): actions is readonly SolverAction[] {
  return !witnessHasAdvanceActions(actions);
}

/**
 * Does this witness carry explicit `settle` actions? True means it is a NATIVE
 * ordered-flow trace: the solver chose every arrival, so the driver must replay
 * it verbatim rather than inserting implied settles of its own.
 */
export function witnessHasSettleActions(actions: readonly LegacySolverAction[]): boolean {
  return actions.some(a => a.type === 'settle');
}

/** Which rulebook a witness was proved under. See the module header. */
export type WitnessVintage = 'native' | 'strict' | 'legacy';

/**
 * Classify a witness from its contents — the whole of the format detection.
 * There is no version field to consult and no caller flag to trust: an
 * `advance` means the parking model (reject), a `settle` means native ordered
 * flow (replay as-is), and dispatch-only means the strict forced-advance model
 * (replay with implied settles). Ambiguity resolves toward `legacy`, the one
 * verdict that refuses rather than mis-replays.
 */
export function classifyWitness(actions: readonly LegacySolverAction[]): WitnessVintage {
  if (witnessHasAdvanceActions(actions)) return 'legacy';
  return witnessHasSettleActions(actions) ? 'native' : 'strict';
}

/** What to tell the user when a witness turns out to be pre-forced-advance. */
export const LEGACY_WITNESS_MESSAGE =
  'witness contains `advance` actions — it was proved under the retired ' +
  'conveyor-parking model and cannot be replayed; re-prove the level at the ' +
  'current solverRev';

// ── Witness loading ─────────────────────────────────────────────────────────

/** Validate a decoded witness file. Throws with a precise reason when malformed. */
export function parseWitnessRecord(raw: unknown): WitnessRecord {
  if (!raw || typeof raw !== 'object') throw new Error('witness is not an object');
  const rec = raw as Partial<WitnessRecord>;
  if (rec.v !== WITNESS_FORMAT_VERSION) {
    throw new Error(`witness is format v${rec.v}, expected v${WITNESS_FORMAT_VERSION}`);
  }
  if (typeof rec.uuid !== 'string') throw new Error('witness has no uuid');
  if (typeof rec.seed !== 'number') throw new Error('witness has no seed');
  if (typeof rec.solverRev !== 'string') throw new Error('witness has no solverRev');
  if (!Array.isArray(rec.witness)) throw new Error('witness has no witness array');
  for (let i = 0; i < rec.witness.length; i++) {
    const a = rec.witness[i] as Partial<LegacySolverAction>;
    if (a?.type === 'advance') continue;
    if (a?.type === 'settle') continue;
    if (a?.type === 'dispatch' && Number.isInteger((a as { attackerId?: number }).attackerId)) continue;
    throw new Error(`witness action ${i} is malformed: ${JSON.stringify(a)}`);
  }
  return rec as WitnessRecord;
}

/**
 * Turn the bytes of a `.json.gz` into its JSON text.
 *
 * Hosts disagree about what `.json.gz` means: vite's dev server labels it
 * `Content-Encoding: gzip`, so the browser has already inflated the body by the
 * time we see it, while a plain static host hands the gzip stream over
 * untouched. Sniffing the gzip magic number is the only thing both agree on.
 */
export async function decodeWitnessBytes(bytes: Uint8Array, label = 'witness'): Promise<string> {
  const gzipped = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (!gzipped) return new TextDecoder().decode(bytes);
  if (typeof DecompressionStream === 'undefined') {
    throw new Error(`${label} arrived gzipped and DecompressionStream is unavailable — needs a Chromium-family browser`);
  }
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).text();
}

/**
 * Fetch and decode `witnesses/{uuid}-{seed}-{rev}.json.gz` — the value a level's
 * `gen.proof.witnessRef` carries verbatim. Served in dev by the
 * `public/witnesses` symlink and in prod by the copy `deploy.sh` makes.
 */
export async function fetchWitness(witnessRef: string): Promise<WitnessRecord> {
  const base = import.meta.env?.BASE_URL ?? '/';
  const url = `${base}${witnessRef.replace(/^\/+/, '')}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`GET ${url} → ${res.status} ${res.statusText}`);
  const text = await decodeWitnessBytes(new Uint8Array(await res.arrayBuffer()), url);
  return parseWitnessRecord(JSON.parse(text));
}

// ── Attacker identity ───────────────────────────────────────────────────────

/**
 * Map solver attacker ids onto live `AttackerData.id`s.
 *
 * `SolverState.initialState` numbers attackers `rawQueueIndex + 1`, then splices
 * hammers in at `hammerIndices` (descending, so earlier splices don't shift
 * later ones) giving each a fresh id past the end. `GameScene` builds its
 * attacker array from the SAME raw queue with the SAME splice, so position `p`
 * in both arrays is the same attacker — that positional correspondence is the
 * whole mapping.
 *
 * @param runtimeIds  live attacker ids, in post-splice queue order
 * @param rawQueueLength  attackers from `generateRawQueue`, before hammers
 * @param hammerIndices  the level's `hammerIndices`, unsorted
 */
export function buildSolverIdMap(
  runtimeIds: readonly number[],
  rawQueueLength: number,
  hammerIndices?: readonly number[],
): Map<number, number> {
  const solverIds: number[] = [];
  for (let i = 0; i < rawQueueLength; i++) solverIds.push(i + 1);
  let nextId = rawQueueLength + 1;
  for (const idx of [...(hammerIndices ?? [])].sort((a, b) => b - a)) {
    solverIds.splice(Math.min(idx, solverIds.length), 0, nextId++);
  }
  if (solverIds.length !== runtimeIds.length) {
    throw new Error(
      `queue length mismatch: solver has ${solverIds.length} attackers, runtime has ${runtimeIds.length} ` +
      '— the runtime is not generating the proven queue',
    );
  }
  const map = new Map<number, number>();
  for (let p = 0; p < solverIds.length; p++) map.set(solverIds[p], runtimeIds[p]);
  return map;
}

// ── Pacing: the arrival schedule of a native witness ────────────────────────

/**
 * Arrival boundary per dispatch, in RUNTIME time-order terms: for every witness
 * index `p` whose dispatch produced a SURVIVOR, the index `q` of the action at
 * whose boundary that rider leaves the model's in-flight ring — an explicit
 * `settle`'s own index, or the index of the dispatch whose age-bound pre-step
 * forces the arrival. A rider still riding when the trace ends maps to
 * `actions.length`. A dispatch that depleted its attacker has NO entry: the
 * model culled it at resolve, its live lap-end is just a fade, and it
 * constrains nothing.
 */
export type WitnessArrivalSchedule = ReadonlyMap<number, number>;

/**
 * Replay a native witness through a MODEL-mode engine and extract its
 * {@link WitnessArrivalSchedule}. Attacker ids are translated through
 * `solverToRuntime` so the schedule speaks the same ids the driver does.
 *
 * Why the driver needs this: the model's age bound lets a trace defer the
 * front rider's arrival for up to `conveyorCap` PLAYER ACTIONS, and one of
 * those actions can be a `settle` — a wait whose live realization is pinned to
 * another rider's real lap-end. Two riders dispatched back-to-back finish
 * their laps back-to-back, so a trace that plans several actions between their
 * arrivals is executable live ONLY if their dispatches were spaced far enough
 * apart. That spacing is exactly what {@link WitnessDriver} derives from this
 * schedule (see {@link WitnessPacingOptions}) — without it, the carnival-circus
 * class of witness loses live on an arrival the model had made room for
 * (2026-09 finding: lv2003, step 202, a 3-slot rider's settle into a stack the
 * trace was still draining).
 *
 * Returns null when the witness does not replay through the model verbatim
 * (a legacy or burst-era archive) — the caller then simply runs unpaced,
 * which is the pre-pacing behaviour.
 */
export function computeWitnessArrivalSchedule(
  initialState: SolverState,
  actions: readonly LegacySolverAction[],
  solverToRuntime: ReadonlyMap<number, number>,
): WitnessArrivalSchedule | null {
  const engine = new OrderedFlowEngine(initialState, { mode: 'model' });
  const schedule = new Map<number, number>();
  /** Dispatch index of each ring rider, parallel to the model ring, oldest first. */
  const ringDispatchIdx: number[] = [];

  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    if (action.type === 'advance') return null; // legacy — unreplayable
    if (action.type === 'settle') {
      const arrival = engine.settle();
      if (arrival === null) return null;
      const p = ringDispatchIdx.shift();
      if (p !== undefined && !arrival.culled) schedule.set(p, i);
      if (engine.over) break;
      continue;
    }
    const runtimeId = solverToRuntime.get(action.attackerId);
    if (runtimeId === undefined) return null;
    const outcome = engine.dispatch(runtimeId);
    if (outcome === null) return null;
    for (const arrival of outcome.forcedArrivals) {
      const p = ringDispatchIdx.shift();
      if (p !== undefined && !arrival.culled) schedule.set(p, i);
    }
    if (outcome.inFlight) ringDispatchIdx.push(i);
    if (engine.over) break;
  }

  // Survivors still riding at the end: their live lap-ends must not land until
  // everything that remains has been executed.
  for (const p of ringDispatchIdx) schedule.set(p, actions.length);
  return schedule;
}

/**
 * Lookahead pacing for a native witness (see {@link computeWitnessArrivalSchedule}).
 *
 * The rule, applied before every dispatch of rider X at witness index `p` with
 * model arrival at `q(p)`: for every rider `j` currently in flight whose model
 * arrival `q(j)` falls strictly between `p` and `q(p)`, the trace performs
 * `c = q(p) - q(j) - 1` actions between the two arrivals, and live those must
 * fit in the gap between the two real lap-ends — which equals the gap between
 * the two DISPATCHES, because laps last the same. So X may not be dispatched
 * sooner than `c * actionBudgetMs + marginMs` after j was. No lap length
 * appears: the lap durations cancel.
 *
 * All times are on the scene's GAME-TIME clock ({@link ReplayView.gameTimeMs}),
 * under which a lap and an entrance always cost the same regardless of the
 * 1×/4×/10× speed toggle. Wall-clock holds were the 2026-09-04 regression:
 * at 10× they outlasted entire laps, arrivals piled up while the cursor was
 * held, and the stack overflowed by step ~11.
 */
export interface WitnessPacingOptions {
  schedule: WitnessArrivalSchedule;
  /**
   * Conservative game-time budget for the driver to execute one witness
   * action once unblocked — dominated by the previous dispatch's entrance
   * animation gating `transitionsClear` (≤ ~680ms for a 5-orb stagger).
   */
  actionBudgetMs?: number;
  /** Fixed margin absorbing entrance-length differences and frame jitter. */
  marginMs?: number;
}

const DEFAULT_ACTION_BUDGET_MS = 700;
const DEFAULT_PACING_MARGIN_MS = 600;

// ── Driver I/O ──────────────────────────────────────────────────────────────

/** Everything the driver needs to see of the live game, as plain data. */
export interface ReplayView {
  /** Runtime id at each lane's *dispatchable* head (`QueueSystem.peekQueue`), null when the lane can't dispatch. */
  laneHeads: readonly (number | null)[];
  /** Runtime id at each lane's raw front, dispatchable or not — for the divergence report. */
  laneFronts: readonly (number | null)[];
  /** Every runtime id still anywhere in the queue. Tells "waiting its turn" from "gone". */
  queueIds: ReadonlySet<number>;
  /** Runtime id per stack slot (null = empty). A multi-slot attacker repeats across its slots. */
  stackSlots: readonly (number | null)[];
  /**
   * The LOGICAL in-flight ring, oldest first — survivors dispatched but not yet
   * arrived. This is the engine's `conveyor`, not the sprites on screen: an
   * attacker that spent its last ammo mid-lap is already gone from the model
   * while its fade animation plays.
   */
  inFlight: readonly number[];
  /**
   * Would the belt take this attacker right now? Belt room gates dispatch
   * (classic rule) — the driver waits on `false` rather than emit a tap the
   * scene would refuse. Backed by the model's ring occupancy and slot widths.
   */
  canAdd: (runtimeId: number) => boolean;
  /** False while a dispatch animation is still in flight (mirrors the autoplay gate). */
  transitionsClear: boolean;
  /**
   * The scene's GAME-TIME clock, in ms: wall time scaled by the live speed
   * multiplier as it accumulates (`Σ delta × multiplier`), i.e. the clock the
   * belt and the entrance tweens actually follow. On this clock a lap always
   * lasts the same ~6.7s and an entrance its usual 200–680ms, whatever the
   * 1×/4×/10× toggle says — which is what makes the pacing gate's fixed
   * budgets meaningful. Wall clock (`nowMs`) would break at 4×/10×: holds
   * sized in wall ms outlast entire accelerated laps, arrivals pile up while
   * the cursor is held, and the stack overflows within the first dozen steps
   * (the 2026-09-04 lv2003 regression of the pacing fix itself).
   */
  gameTimeMs: number;
  /** Cumulative attackers that have arrived on the stack. */
  stackArrivals: number;
  cubesRemaining: number;
  gameOver: boolean;
}

/** What the driver wants the scene to do this frame. */
export type ReplayCommand =
  | { kind: 'dispatch-queue'; lane: number; solverId: number; runtimeId: number }
  | { kind: 'dispatch-stack'; slotIndex: number; solverId: number; runtimeId: number }
  | { kind: 'wait'; why: string }
  | { kind: 'finished' };

export type ReplayStatus = 'running' | 'won' | 'failed';

/** A snapshot of the game at the moment the replay gave up. */
export interface ReplayStateSummary {
  laneHeads: readonly (number | null)[];
  laneFronts: readonly (number | null)[];
  inFlight: readonly number[];
  stackSlots: readonly (number | null)[];
  cubesRemaining: number;
  stackArrivals: number;
  advancesConsumed: number;
}

export interface DivergenceReport {
  /** Index into the witness of the action that could not be carried out. */
  step: number;
  total: number;
  /** Machine-readable cause. */
  reason:
    | 'game-over'
    | 'attacker-missing'
    | 'unmapped-attacker'
    | 'stalled'
    | 'dispatch-rejected'
    | 'actions-exhausted';
  /** Human-readable one-liner. */
  message: string;
  /** The witness action we were trying to perform, when there was one. */
  expected?: LegacySolverAction;
  /** Live id the expected action's `attackerId` maps to. */
  expectedRuntimeId?: number;
  state: ReplayStateSummary;
}

export interface WitnessDriverOptions {
  /**
   * Give up after this many ms of wall clock in which NOTHING observable
   * changed — no cube cleared, no ring movement, no arrival. Waiting for a lap
   * to finish is normal and does not count against it.
   */
  stallMs?: number;
  /**
   * Debug escape hatch (`&serial=1`): wait for the in-flight ring to empty
   * before EVERY dispatch, not just after a survivor. Ordered flow does not
   * need this — `impliedSettle` already reproduces a strict-model trace — but it
   * is useful when bisecting a divergence. Default off.
   */
  serial?: boolean;
  /**
   * Insert the implied `settle` after any dispatch that produced a survivor,
   * which is how a strict-model (forced-advance) witness maps onto ordered flow.
   *
   * DEFAULTS BY DETECTION, not by constant: omitted, the driver reads the
   * witness ({@link classifyWitness}) and turns implied settles on for a
   * dispatch-only trace and off for one carrying native `settle` actions.
   * Passing it explicitly overrides that — useful only for bisecting.
   */
  impliedSettle?: boolean;
  /**
   * Lookahead pacing for a native witness — spaces dispatches so every model
   * arrival's live lap-end lands only after the actions the trace put before
   * it. Omitted, the driver runs unpaced (correct for strict-model witnesses,
   * whose implied settles already serialize the ring). See
   * {@link WitnessPacingOptions} and {@link computeWitnessArrivalSchedule}.
   */
  pacing?: WitnessPacingOptions;
}

const DEFAULT_STALL_MS = 15_000;

// ── Driver ──────────────────────────────────────────────────────────────────

/**
 * Walks a witness against the live game, one readiness check at a time.
 *
 * The scene calls {@link next} every frame and performs whatever comes back,
 * then reports the outcome via {@link onDispatched} / {@link onDispatchRejected}.
 * The driver never touches the game itself, which is what makes it testable
 * against a hand-built {@link ReplayView}.
 */
export class WitnessDriver {
  readonly total: number;

  private readonly actions: readonly LegacySolverAction[];
  private readonly idMap: ReadonlyMap<number, number>;
  private readonly stallMs: number;
  private readonly serial: boolean;
  private readonly impliedSettle: boolean;
  private readonly vintage: WitnessVintage;
  private readonly pacing?: WitnessPacingOptions;
  private readonly actionBudgetMs: number;
  private readonly pacingMarginMs: number;
  /** Witness index and GAME-TIME of each surviving dispatch, per runtime id. */
  private readonly dispatchRecord = new Map<number, { index: number; atGameMs: number }>();
  /** The game clock of the most recent {@link next} view — timestamps onDispatched. */
  private lastGameTimeMs = 0;

  private _index = 0;
  private _advancesConsumed = 0;
  private _settlesAwaited = 0;
  private _status: ReplayStatus = 'running';
  private _failure?: DivergenceReport;
  /**
   * Runtime id we are waiting to see leave the in-flight ring — the implied
   * `settle` of a strict-model witness. Null when nothing is pending.
   */
  private pendingSettleId: number | null = null;

  /** Last observed state fingerprint and when it last changed — the stall detector. */
  private fingerprint = '';
  private fingerprintAt = 0;

  constructor(
    actions: readonly LegacySolverAction[],
    solverToRuntime: ReadonlyMap<number, number>,
    options: WitnessDriverOptions = {},
  ) {
    this.actions = actions;
    this.idMap = solverToRuntime;
    this.total = actions.length;
    this.stallMs = options.stallMs ?? DEFAULT_STALL_MS;
    this.serial = options.serial ?? false;
    this.vintage = classifyWitness(actions);
    // A native trace already says when every rider arrives; adding implied
    // settles on top would arrive them twice as far as the pacing is concerned
    // and turn legal burst lines into stalls.
    this.impliedSettle = options.impliedSettle ?? this.vintage !== 'native';
    this.pacing = options.pacing;
    this.actionBudgetMs = options.pacing?.actionBudgetMs ?? DEFAULT_ACTION_BUDGET_MS;
    this.pacingMarginMs = options.pacing?.marginMs ?? DEFAULT_PACING_MARGIN_MS;
  }

  /** Whether this driver waits for an empty ring before each dispatch (debug only). */
  get serialMode(): boolean { return this.serial; }

  /** Whether a strict-model witness's implied settles are being inserted. */
  get impliedSettleMode(): boolean { return this.impliedSettle; }

  /** Which rulebook this witness was proved under — detected from its contents. */
  get witnessVintage(): WitnessVintage { return this.vintage; }

  get index(): number { return this._index; }
  get status(): ReplayStatus { return this._status; }
  get failure(): DivergenceReport | undefined { return this._failure; }
  get advancesConsumed(): number { return this._advancesConsumed; }
  /** Native `settle` actions the driver has waited out so far. */
  get settlesAwaited(): number { return this._settlesAwaited; }

  /**
   * Decide what to do right now. Consumes legacy `advance` actions (which need
   * no input) inline, so the returned command always concerns a real dispatch.
   */
  next(view: ReplayView, nowMs: number): ReplayCommand {
    if (this._status !== 'running') return { kind: 'finished' };

    this.lastGameTimeMs = view.gameTimeMs;
    this.trackProgress(view, nowMs);

    // The board clearing wins outright, even with actions to spare — the
    // runtime simply cleared what the solver still had planned.
    if (view.cubesRemaining === 0) {
      this._status = 'won';
      return { kind: 'finished' };
    }
    if (view.gameOver) {
      return this.fail(view, 'game-over', 'the game ended (loss) before the witness ran out');
    }

    // Consume everything that needs no tap, until a dispatch (or the end).
    //
    //   `advance` — retired; nothing to do but count it and move on. Only a
    //     legacy witness has any, and callers are expected to have rejected
    //     those already.
    //   `settle`  — a native ordered-flow trace naming an arrival. The driver
    //     cannot *cause* an arrival (the game settles on its own clock), so it
    //     realises the event by WAITING for the ring's oldest rider to leave.
    //     An already-empty ring means the arrival has happened; consume it.
    while (this._index < this.actions.length) {
      const pending = this.actions[this._index];
      if (pending.type === 'advance') {
        this._advancesConsumed++;
        this._index++;
        continue;
      }
      if (pending.type === 'settle') {
        if (this.pendingSettleId === null) {
          const oldest = view.inFlight[0];
          if (oldest === undefined) {
            this._settlesAwaited++;
            this._index++;
            continue;
          }
          this.pendingSettleId = oldest;
        }
        if (view.inFlight.includes(this.pendingSettleId)) {
          return this.waitOrStall(
            view, nowMs, `waiting for the settle of #${this.pendingSettleId}`, pending,
          );
        }
        this.pendingSettleId = null;
        this._settlesAwaited++;
        this._index++;
        continue;
      }
      break;
    }

    if (this._index >= this.actions.length) {
      return this.fail(
        view, 'actions-exhausted',
        `witness ran out with ${view.cubesRemaining} cubes still on the board`,
      );
    }

    const action = this.actions[this._index];
    /* istanbul ignore next — the loop above consumes every non-dispatch kind */
    if (action.type !== 'dispatch') {
      return this.fail(view, 'dispatch-rejected', `unexpected action type ${action.type}`);
    }

    const runtimeId = this.idMap.get(action.attackerId);
    if (runtimeId === undefined) {
      return this.fail(
        view, 'unmapped-attacker',
        `solver attacker ${action.attackerId} has no runtime counterpart`, action,
      );
    }

    // The implied `settle` of a strict-model witness: the survivor the previous
    // dispatch produced arrives on the stack before anything else happens.
    if (this.pendingSettleId !== null) {
      if (view.inFlight.includes(this.pendingSettleId)) {
        return this.waitOrStall(
          view, nowMs,
          `waiting for the implied settle of #${this.pendingSettleId}`, action, runtimeId,
        );
      }
      this.pendingSettleId = null;
    }

    const lane = view.laneHeads.indexOf(runtimeId);
    const slotIndex = view.stackSlots.indexOf(runtimeId);

    if (lane < 0 && slotIndex < 0) {
      // Not dispatchable yet. Riding the belt or queued behind others is fine —
      // it will become dispatchable. Vanished is a divergence.
      if (view.inFlight.includes(runtimeId)) {
        return this.waitOrStall(view, nowMs, 'target still in flight', action, runtimeId);
      }
      if (view.queueIds.has(runtimeId)) {
        return this.waitOrStall(view, nowMs, 'target queued but not at a dispatchable lane head', action, runtimeId);
      }
      return this.fail(
        view, 'attacker-missing',
        `attacker ${runtimeId} (solver ${action.attackerId}) is not in the queue, in flight, or in the stack`,
        action, runtimeId,
      );
    }

    if (!view.transitionsClear) {
      return this.waitOrStall(view, nowMs, 'dispatch animation in flight', action, runtimeId);
    }
    if (this.serial && view.inFlight.length > 0) {
      return this.waitOrStall(view, nowMs, 'serial mode: waiting for the ring to empty', action, runtimeId);
    }
    // Belt room gates dispatch (classic rule): a tap while the belt lacks room
    // is refused by the scene, so the driver waits for the next lap-end to free
    // the slots instead of emitting a tap that would bounce. This is also how a
    // burst-era witness (proved when a full belt forced arrivals) is realized
    // live — the arrivals it relied on happen at real lap-ends first.
    if (!view.canAdd(runtimeId)) {
      return this.waitOrStall(view, nowMs, 'waiting for belt room', action, runtimeId);
    }
    // Lookahead pacing (native witnesses): this rider's real lap-end must not
    // land before the actions the trace put ahead of its arrival. Lap-end gaps
    // equal dispatch gaps, so the hold is expressed purely in dispatch spacing
    // — on the GAME-TIME clock, so the budgets mean the same thing at every
    // speed step (see ReplayView.gameTimeMs).
    const holdUntil = this.pacingHoldUntil(view);
    if (holdUntil !== null && view.gameTimeMs < holdUntil) {
      return this.waitOrStall(
        view, nowMs,
        `pacing: spacing this dispatch ${Math.ceil(holdUntil - view.gameTimeMs)} game-ms behind a rider arriving before it`,
        action, runtimeId,
      );
    }

    return lane >= 0
      ? { kind: 'dispatch-queue', lane, solverId: action.attackerId, runtimeId }
      : { kind: 'dispatch-stack', slotIndex, solverId: action.attackerId, runtimeId };
  }

  /**
   * The scene performed the last command successfully — move to the next action.
   *
   * @param survivorRuntimeId  the dispatched attacker, when it survived its lap
   *   and joined the in-flight ring. Under {@link WitnessDriverOptions.impliedSettle}
   *   the driver then waits for it to arrive before the next action, which is
   *   how a strict-model witness's "survivor lands immediately" rule is honoured.
   */
  onDispatched(survivorRuntimeId?: number): void {
    if (this._status !== 'running') return;
    if (survivorRuntimeId !== undefined) {
      this.dispatchRecord.set(survivorRuntimeId, { index: this._index, atGameMs: this.lastGameTimeMs });
    }
    this._index++;
    if (this.impliedSettle && survivorRuntimeId !== undefined) {
      this.pendingSettleId = survivorRuntimeId;
    }
  }

  /**
   * The pacing rule of {@link WitnessPacingOptions}, evaluated for the dispatch
   * at the current index: the earliest GAME-TIME at which it may go out, or
   * null when nothing constrains it. Game-time, not wall time: the budgets
   * describe laps and entrances, and only on the scaled clock do those cost
   * the same at 1×, 4× and 10×.
   */
  private pacingHoldUntil(view: ReplayView): number | null {
    if (!this.pacing) return null;
    const p = this._index;
    // No entry = the model culled this rider at resolve — its live lap-end is
    // a fade, not an arrival, and needs no spacing.
    const qX = this.pacing.schedule.get(p);
    if (qX === undefined) return null;
    let holdUntil: number | null = null;
    for (const jid of view.inFlight) {
      const rec = this.dispatchRecord.get(jid);
      if (!rec) continue;
      const qJ = this.pacing.schedule.get(rec.index);
      // qJ <= p: j's arrival is already overdue (live lag) — a LATE arrival
      // only ever lands in a stack the trace has since drained further; safe.
      if (qJ === undefined || qJ <= p || qJ >= qX) continue;
      const between = qX - qJ - 1;
      const need = rec.atGameMs + between * this.actionBudgetMs + this.pacingMarginMs;
      if (holdUntil === null || need > holdUntil) holdUntil = need;
    }
    return holdUntil;
  }

  /** The scene could not perform the last command. That is a divergence. */
  onDispatchRejected(view: ReplayView, reason: string): void {
    const action = this.actions[this._index];
    const runtimeId = action?.type === 'dispatch' ? this.idMap.get(action.attackerId) : undefined;
    this.fail(view, 'dispatch-rejected', `the game refused the dispatch: ${reason}`, action, runtimeId);
  }

  /** One-line progress readout for the on-screen status. */
  progressLabel(): string {
    const state = this._status === 'running' ? '' : ` ${this._status.toUpperCase()}`;
    return `witness ${Math.min(this._index, this.total)}/${this.total}${state}`;
  }

  /** Reset the stall clock whenever anything observable moves. */
  private trackProgress(view: ReplayView, nowMs: number): void {
    const fp = [
      this._index,
      view.stackArrivals,
      view.cubesRemaining,
      view.inFlight.join('.'),
      view.laneHeads.join('.'),
      view.stackSlots.join('.'),
    ].join('|');
    if (fp !== this.fingerprint) {
      this.fingerprint = fp;
      this.fingerprintAt = nowMs;
    }
  }

  private waitOrStall(
    view: ReplayView,
    nowMs: number,
    why: string,
    action: LegacySolverAction,
    runtimeId?: number,
  ): ReplayCommand {
    if (nowMs - this.fingerprintAt > this.stallMs) {
      return this.fail(
        view, 'stalled',
        `nothing changed for ${Math.round((nowMs - this.fingerprintAt) / 1000)}s while ${why}`,
        action, runtimeId,
      );
    }
    return { kind: 'wait', why };
  }

  private fail(
    view: ReplayView,
    reason: DivergenceReport['reason'],
    message: string,
    expected?: LegacySolverAction,
    expectedRuntimeId?: number,
  ): ReplayCommand {
    if (this._status === 'running') {
      this._status = 'failed';
      this._failure = {
        step: this._index,
        total: this.total,
        reason,
        message,
        expected,
        expectedRuntimeId,
        state: {
          laneHeads: [...view.laneHeads],
          laneFronts: [...view.laneFronts],
          inFlight: [...view.inFlight],
          stackSlots: [...view.stackSlots],
          cubesRemaining: view.cubesRemaining,
          stackArrivals: view.stackArrivals,
          advancesConsumed: this._advancesConsumed,
        },
      };
    }
    return { kind: 'finished' };
  }
}

// ── Reporting ───────────────────────────────────────────────────────────────

function describeAction(action: LegacySolverAction | undefined, runtimeId?: number): string {
  if (!action) return '(none)';
  if (action.type === 'advance') return 'advance';
  if (action.type === 'settle') return 'settle';
  const mapped = runtimeId === undefined ? '' : ` → runtime #${runtimeId}`;
  return `dispatch solver #${action.attackerId}${mapped}`;
}

function describeSlots(ids: readonly (number | null)[]): string {
  return ids.length === 0 ? '(none)' : ids.map(id => (id === null ? '-' : String(id))).join(', ');
}

/** Render a divergence as the multi-line console block the replay logs on FAIL. */
export function formatDivergence(report: DivergenceReport): string {
  const s = report.state;
  return [
    `${REPLAY_LOG_PREFIX} FAIL at step ${report.step}/${report.total} — ${report.reason}: ${report.message}`,
    `  expected:      ${describeAction(report.expected, report.expectedRuntimeId)}`,
    `  lane heads:    ${describeSlots(s.laneHeads)}   (dispatchable)`,
    `  lane fronts:   ${describeSlots(s.laneFronts)}   (incl. frozen / not-ready)`,
    `  in flight:     ${s.inFlight.length === 0 ? '(empty)' : s.inFlight.join(', ')}   (oldest first)`,
    `  stack:         ${describeSlots(s.stackSlots)}`,
    `  cubes left:    ${s.cubesRemaining}`,
    `  arrivals:      ${s.stackArrivals}` +
      (s.advancesConsumed > 0 ? ` (witness also carried ${s.advancesConsumed} retired advances)` : ''),
  ].join('\n');
}
